<template>
  <div
    class="modal fade fixed-right modal-import-export-draw"
    tabindex="-1"
    role="dialog"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-vertical" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Import/Export Graduates</h5>
          <button type="button" class="close" data-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div class="modal-body">
          <h4>Export</h4>
          <p>Export current program placement data and agencies used within the system.</p>
          <div class="row mb-2 d-flex align-items-center">
            <div class="col">
              <strong>Graduates</strong>
            </div>
            <div class="col-auto">
              <ladda-button :click="exportGraduates" :validate="true" class="btn btn-sm btn-primary">Download</ladda-button>
            </div>
          </div>
          <div class="row">
            <div class="col d-flex align-items-center">
              <strong>Agencies Reference Data</strong>
            </div>
            <div class="col-auto">
              <ladda-button :click="exportAgencyReferenceData" class="btn btn-sm btn-primary">Download</ladda-button>
            </div>
          </div>
          <hr />
          <h4>Import Graduates</h4>
          <p>Import the graduates file to add or update graduates within this program.</p>
          <div class="form-group">
            <label class="control-label">Select File</label>
            <div class="row d-flex align-items-center">
              <div class="col">
                <div class="custom-file">
                  <input
                    type="file"
                    id="importGraduateFile"
                    ref="file"
                    @change="fileSelectionChanged"
                    class="custom-file-input"
                  />
                  <label
                    for="importGraduateFile"
                    class="custom-file-label"
                  >{{ importFile ? importFile.name : 'Choose file' }}</label>
                </div>
              </div>
              <div class="col-auto">
                <ladda-button :click="importGraduates" :validate="true" class="btn btn-info">Upload</ladda-button>
              </div>
            </div>
          </div>
          <template v-if="importSuccess">
            <p
              class="text-success"
            >Your import is being processed. You will receive an email once it completes.</p>
          </template>
          <template v-if="importErrors.length > 0">
            <p
              class="text-danger"
            >{{ importErrors.length }} errors have been found, please fix them before attempting to upload the file again.</p>
            <div class="row">
              <div class="col d-flex align-items-center">
                <h4>Errors:</h4>
              </div>
              <div class="col-auto">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  @click="downloadImportErrors"
                >
                  <font-awesome :icon="['far', 'cloud-download']" />Download Errors
                </button>
              </div>
            </div>
            <ul class="import-errors">
              <li v-for="(error, index) in importErrors" :key="index">{{ error }}</li>
            </ul>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { http, alert } from 'utils'
import moment from 'moment'
import { snakeCase } from 'lodash'
import { saveAs } from 'file-saver'
import { programMethods } from 'vx/helpers'

export default {
  props: {
    program: {
      required: true
    }
  },
  data () {
    return {
      importFile: null,
      importErrors: [],
      importSuccess: false
    }
  },
  methods: {
    ...programMethods,

    async exportAgencyReferenceData () {
      const response = await http.get('/agencies/reference-data', { responseType: 'blob' })

      saveAs(response.data, `agencies-reference-data-${moment().format('YYYY-MM-DD-HH-mm-ss')}.csv`)
    },

    async exportGraduates () {
      const response = await http.get(`/programs/${this.program.id}/export-graduates`, { responseType: 'blob' })

      saveAs(response.data, `graduate-export-${snakeCase(this.program.name)}-${moment().format('YYYY-MM-DD-HH-mm-ss')}.csv`)
    },

    fileSelectionChanged (event) {
      if (event.target.files.length === 0) {
        this.importFile = null

        return
      }

      this.importFile = event.target.files.item(0)
    },

    async importGraduates () {
      if (
        !await alert.confirm({
          title: 'Import Graduates',
          text: 'Importing this file will overwrite any existing graduate data for this program if they already exist within the system. Are you sure you want to continue with the import?'
        })
      ) {
        return
      }

      try {
        this.importErrors = []
        this.importSuccess = false

        await this.importProgramGraduates({
          program: this.program,
          file: this.importFile
        })

        this.importSuccess = true

        this.$emit('imported')
      } catch (error) {
        this.$noty.error('Something went wrong importing the graduates. Check the file and try again.')

        if (error.response && error.response.status === 422) {
          this.importErrors = Object.values(error.response.data.errors).reduce((errors, rowErrors) => [...errors, ...rowErrors], [])
        }
      } finally {
        this.$refs.file.value = ''
        this.importFile = null
      }
    },

    downloadImportErrors () {
      const element = document.createElement('a')
      element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(this.importErrors.join('\n')))
      element.setAttribute('download', `${snakeCase(this.program.name)}_graduate_import_errors_${moment().format('YYYY_MM_DD')}.txt`)

      element.style.display = 'none'
      document.body.appendChild(element)

      element.click()

      document.body.removeChild(element)
    }
  }
}
</script>
